import { useQueryUser } from '@heidi/workspace/data';
import { useAtomValue } from 'jotai';

import type { CreditAllowance } from '../../contracts';
import {
  applyComplimentaryTrialEndDevOverride,
  complimentaryTrialEndDevOverrideAtom,
} from '../../data/billing-dev/complimentary-trial-end-overrides';
import { useReverseTrial } from '../../data/injected-billing';
import {
  type ComplimentaryTrialEndState,
  deriveComplimentaryTrialEndState,
} from '../../data/query/complimentary-trial-end.utils';
import { useQueryCreditAllowance } from '../../data/query/useQueryCreditAllowance';
import { useQueryUserBillingInfo } from '../../data/query/useQueryUserBillingInfo';
import { isHeidi2OptInCreditUsageEnabled } from './is-credit-usage-surface-enabled';
import { isMaxBillingSeat } from './is-max-billing-seat';
import { showsCreditUsageMeterTrialCountdown } from './shows-credit-usage-meter-trial-countdown';

export type ComplimentaryTrialEndFlags = {
  /** `release-heidi-2-opt-in` (app-resolved, PostHog). */
  isHeidi2OptInEnabled: boolean;
  /** `release-growth-heidi-2-trial-end` (app-resolved, PostHog). */
  isTrialEndEnabled: boolean;
};

/**
 * Last-week state of the 30-day complimentary Max usage, from an allowance the
 * caller already holds. Members on the 14-day trial keep their own trial
 * countdown, Max seats have nothing to upgrade to, and managed members' billing
 * is handled centrally, so they never see a self-checkout upsell.
 */
export function useComplimentaryTrialEndState({
  isHeidi2OptInEnabled,
  isTrialEndEnabled,
  allowance,
}: ComplimentaryTrialEndFlags & {
  allowance: CreditAllowance | null | undefined;
}): ComplimentaryTrialEndState | null {
  const { data: user } = useQueryUser();
  const { data: billingInfo } = useQueryUserBillingInfo();
  const { isClinicianTrialUser, isClinicianPracticeTrialUser } = useReverseTrial();
  const devOverride = useAtomValue(complimentaryTrialEndDevOverrideAtom);
  const now = new Date();

  const forced = applyComplimentaryTrialEndDevOverride(devOverride, now);
  if (forced != null) return forced;

  return deriveComplimentaryTrialEndState({
    allowance,
    isCohortEnabled: isHeidi2OptInCreditUsageEnabled({
      isHeidi2OptInEnabled,
      heidiVersion: billingInfo?.heidi_version,
    }),
    isTrialEndEnabled,
    isExcluded:
      !!user?.is_managed ||
      isMaxBillingSeat(billingInfo) ||
      showsCreditUsageMeterTrialCountdown({
        isClinicianTrialUser,
        isClinicianPracticeTrialUser,
      }),
    now,
  });
}

/** {@link useComplimentaryTrialEndState}, reading the allowance itself once the flags allow. */
export function useQueriedComplimentaryTrialEndState(
  flags: ComplimentaryTrialEndFlags
): ComplimentaryTrialEndState | null {
  const { data: user } = useQueryUser();
  const { data: allowance } = useQueryCreditAllowance({
    hasUser: !!user,
    enabled: flags.isTrialEndEnabled && flags.isHeidi2OptInEnabled && !user?.is_managed,
  });
  return useComplimentaryTrialEndState({ ...flags, allowance });
}
